import { isEmpty, take } from 'lodash-es';

import { SearchObjectOptions } from '@@core/models/helper.model';
import { environment } from '@@environments/environment';

export const environmentInit = (): void => {
	const envName = environment.production ? 'PRODUCTION' : 'DEVELOPMENT';
	const envMessage = `SeaLights Web App is running with ${envName} environment file`;
	const messageStyle = `
		background-color: #fdf3cf;
		border: 1px solid #673ab7;
		border-radius: 4px;
		color: #673ab7;
		font-family: georgia;
		font-size: 14px;
		font-weight: bold;
		margin: 8px;
		padding: 8px;
	`;

	console.log(`%c${envMessage}`, messageStyle);
};

export const copyToClipboard = (text: string): Promise<void> => {
	return navigator.clipboard.writeText(text);
};

export const downloadAsFile = (content: ArrayBuffer | string, name: string, type: string): void => {
	const a = document.createElement('a');
	const file = new Blob([content], { type });

	a.href = URL.createObjectURL(file);
	a.download = name;
	document.body.appendChild(a); // Firefox needs the element to be in the page, not only in memory
	a.click();
	URL.revokeObjectURL(a.href);
	a.remove();
};

export const mailTo = (email: string): void => {
	const a = document.createElement('a');

	a.href = `mailto:${email}`;
	document.body.appendChild(a); // Firefox needs the element to be in the page, not only in memory
	a.click();
	URL.revokeObjectURL(a.href);
	a.remove();
};

export const navigateInNewTab = (relativeUrl: string): void => {
	const fullUrl = `${location.protocol}//${location.host}${relativeUrl}`;
	window.open(fullUrl, '_blank');
};

export const hashWithSha256 = async (text: string | number): Promise<string> => {
	const buffer: ArrayBuffer = await window.crypto.subtle.digest(
		'SHA-256',
		new TextEncoder().encode(text?.toString?.()),
	);
	const hashArray = Array.from(new Uint8Array(buffer)); // convert buffer to byte array
	const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
	return hashHex;
};

export const isChromeBrowser = (): boolean => {
	const userAgent = window.navigator.userAgent.toLowerCase();

	return userAgent.indexOf('chrome') > -1 && userAgent.indexOf('firefox') === -1 && userAgent.indexOf('edg') === -1;
};

export const searchObject = (
	o: object,
	searchTerm: string,
	options: SearchObjectOptions = defaultSearchObjectOptions,
): any[] => {
	const realValues = Object.values(o ?? {}).filter(value => value !== null && value !== undefined);
	let matches = [];

	if (!options.caseSensitive) {
		searchTerm = searchTerm.toLowerCase();
	}

	for (const value of realValues) {
		if (typeof value === 'object') {
			matches = matches.concat(searchObject(value, searchTerm));
		} else {
			const found = (value.toString().toLowerCase() as string).includes(searchTerm);

			if (found) {
				matches.push(value);
			}
		}
	}

	return matches;
};

const defaultSearchObjectOptions: SearchObjectOptions = {
	caseSensitive: false,
};

/**
 * Convert list of items to string like for ex. 'a, b, c and 10 others'
 */
export const listToNameCluster = <T>(
	items: T[],
	itemsToTake: number,
	additionWords: [string, string],
	stringMapper: (item: T) => string,
): string => {
	// Item list is not an array or empty item list? Sorry, your mistake!
	if (!(Array.isArray(items) && items.length)) {
		return '';
	}

	// cannot take 0 or less. Default is 1
	if (itemsToTake <= 0) {
		itemsToTake = 1;
	}

	// This is kind of optional. Give a chance to replace 10 others with something like 10 other users or groups
	if (!(Array.isArray(additionWords) && additionWords.length === 2)) {
		additionWords = ['other', 'others'];
	}

	// Set singular and plural words
	const additionalWordSingular = additionWords[0];
	const additionalWordPlural = additionWords[1];

	// Take items
	const itemsTaken = take(items, itemsToTake);

	// Map the items to kind of array of strings by user defined way
	const mappedStrings = itemsTaken.map(stringMapper);

	// // If it is only one, return it.
	// if (mappedStrings.length === 1) {
	// 	return mappedStrings.toString();
	// }

	let result: string;

	// See how many items left to display.
	const otherItemsNumber = items.length - itemsToTake;

	// if more than 1 then display all item strings that user asked to take and add items left (plural variant)
	// If it is only 1 left, then display all item strings that user asked to take and add items left (singular variant)
	// Less than 1 left, display all item strings that user asked to take. No additional strings
	if (otherItemsNumber > 1) {
		result = mappedStrings.join(', ');
		result += ` and ${otherItemsNumber} ${additionalWordPlural}`;
	} else if (otherItemsNumber === 1) {
		result = mappedStrings.join(', ');
		result += ` and ${otherItemsNumber} ${additionalWordSingular}`;
	} else {
		if (mappedStrings.length === 1) {
			return mappedStrings.toString();
		}
		const allMappedStringsButLast = take(mappedStrings, mappedStrings.length - 1);
		result = `${allMappedStringsButLast.join(', ')} and ${mappedStrings[mappedStrings.length - 1]}`;
	}

	return result;
};

// TODO: this should not be here. Header/Cockpit specific. Temporary position until we have proper localStorage service/store
export const getHideSuperuserMessagesFlag = (): boolean =>
	localStorage.getItem('hide-superuser-messages') ? true : false;

export const isLocalHost = (): boolean => {
	const url = window.location.hostname;
	return url.includes('localhost') || url.includes('127.0.0.1');
};

/**
 * Checks if a value is null, undefined, or an empty object
 * @param value The value to check
 * @returns True if the value is null, undefined, or an empty object; otherwise, false
 */
export const isNullUndefinedOrEmptyObject = (value: any): boolean =>
	value === null || value === undefined || (typeof value === 'object' && isEmpty(value));
